import {
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Conversation } from '../../models/chat/chatModel';
import { ChatService } from '../../models/chat/chatService';
import { StorageService } from '../../core/storage/storageService';
import { User } from '../../models/users/user';

class UserInfo {
  constructor(
    public name: string,
    public email: string,
    public phone: string,
  ) {}
  isEmpty(): boolean {
    return this.name === '' && this.email === '';
  }
}

@Component({
  selector: 'app-chat-window',
  templateUrl: './chat-window.component.html',
  styleUrls: ['./chat-window.component.scss'],
  standalone: false,
})
export class ChatWindowComponent implements OnInit, OnDestroy {
  chatActive: boolean = false;

  userInfo = new UserInfo('', '', '');

  conversationId: string | undefined;
  role: string = 'guest';

  chatWindow: ElementRef;
  chatMessageBox: ElementRef;
  chatWindowDiv: any;
  chatMessageBoxDiv: any;

  oChat: any;

  lastMessageId: number = -1;
  guestName: string | undefined = undefined;
  supportName: string | undefined = undefined;

  name: string = '';
  email: string = '';

  pingConversationIntervalId: number = 0;

  // CONFIG
  // TODO: Pull these from the server
  POLL_CONVERSATION_SECONDS = 15;
  POLL_QUEUE_SECONDS = 2;
  POLL_MESSAGES_SECONDS = 2;
  DISCONNECTED_WAIT_SECONDS = 30;
  /////

  ROLE_GUEST = 'guest';
  ROLE_SUPPORT = 'support';
  ROLE_SYSTEM = 'system';

  constructor(
    private renderer: Renderer2,
    private chat: ChatService,
    private storage: StorageService,
    private dialogRef: MatDialogRef<ChatWindowComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.chatWindow = ElementRef.prototype;
    this.chatMessageBox = ElementRef.prototype;

    if (data != null) {
      if (data.conversationId === '') data.conversationId = null;

      this.role = data.role;
      this.conversationId = data.conversationId;
    }
  }

  async ngOnDestroy(): Promise<void> {
    if (this.conversationId == null) return;

    if (this.role === this.ROLE_SUPPORT) {
      await this.chat.unjoin(this.conversationId, this.name);
      this.storage.unsetConversationId();
    }
  }

  ngOnInit() {
    const user: User | undefined = this.storage.getCurrentUser();
    if (user != null) {
      this.userInfo.name = `${user.firstName} ${user.lastName}`;
      this.userInfo.email = user.emailAddress;
      this.userInfo.phone = user.phone ?? '';
      this.activate();
    }
  }

  // TODO (jacob): Can this be eliminated?
  @ViewChild('chatWindow', { static: false }) set chatWindowContent(
    content: ElementRef,
  ) {
    if (content) {
      this.chatWindow = content;
      this.chatWindowDiv = this.chatWindow.nativeElement;
      this.initializeChatConversation();
    }
  }

  // TODO (jacob): Can this be eliminated?
  @ViewChild('chatMessageBox', { static: false }) set chatMessageBoxContent(
    content: ElementRef,
  ) {
    if (content) {
      this.chatMessageBox = content;
      this.chatMessageBoxDiv = this.chatMessageBox.nativeElement;

      this.initializeChatConversation();
    }
  }

  private initializeChatConversation() {
    // The conversation window and the message window must both be initialized before this function can proceed.
    // Since they get initialized only after they become visible (and they get initialized separately), they
    // will not both be ready at exactly the same time.  So, both initlization functions call this function, and
    // then basically only the last call will work. This is very simple, but maybe this is a little hacky?
    // Alternate ideas:
    // 1. set an interval that checks for both elements to be ready
    // 2. wrap them in promises

    Promise.resolve()
      .then(async (): Promise<Conversation | null> => {
        if (
          this.chatWindowDiv === undefined ||
          this.chatMessageBoxDiv === undefined
        ) {
          return null;
        }

        this.name = this.userInfo.name;
        this.email = this.userInfo.email;

        if (this.conversationId != null) {
          return this.chat.join(this.name, this.conversationId);
        } else {
          this.appendComment(
            this.ROLE_SYSTEM,
            '',
            'Welcome to Swipesum Chat, ' + this.name + '!',
          );
          this.appendComment(
            this.ROLE_SYSTEM,
            '',
            'We are finding the next representative to assist you...',
          );

          return this.chat.start(this.name, this.email);
        }
      })
      .then((convo: Conversation | null) => {
        if (convo != null) {
          this.storage.setConversationId(convo.id);
          this.conversationId = convo.id;
          this.initializeConversation(convo);
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  public activate() {
    this.chatActive = true;
    // chat will be initialized when @ViewChild detects the chat window
    // See this.initializeChatConversation()
  }

  public join() {
    this.role = 'support';
    this.userInfo.name = 'Swipesum';
    this.userInfo.email = 'support@swipesum.com';
    this.chatActive = true;
    // chat will be initialized when @ViewChild detects the chat window
    // See this.initializeChatConversation()
  }

  async close() {
    if (this.conversationId && this.role === this.ROLE_SUPPORT) {
      await this.unjoin();
    }
    this.dialogRef.close();
  }

  async end() {
    if (this.conversationId == null) return;

    window.clearInterval(this.pingConversationIntervalId);

    await this.chat.end(this.conversationId, this.role, 'disconnected');

    this.storage.unsetConversationId();
    this.conversationId = undefined;
  }

  async unjoin() {
    if (this.conversationId == null) return;

    window.clearInterval(this.pingConversationIntervalId);
    await this.chat.unjoin(this.conversationId, this.role);
    this.conversationId = undefined;
    this.storage.unsetConversationId();
  }

  async send() {
    if (this.conversationId == null) return;

    const stripHTMLregex = /(<([^>]+)>)/gi;
    const message = this.chatMessageBoxDiv.value.replace(stripHTMLregex, '');

    if (/^\s*$/g.test(message)) return;

    this.appendComment(this.role, this.name, message);

    const response = await this.chat.postMessage(
      this.conversationId,
      this.role,
      message,
    );
    this.lastMessageId = response.id;
    this.chatMessageBoxDiv.value = '';
  }

  private async initializeConversation(conversation: Conversation) {
    if (this.conversationId == null) return;

    try {
      await this.ping();
    } catch (e) {
      // TODO (jacob):
    }

    // Now that we're connected, switch over to pinging for activity to see if the conversation is still active.
    let that = this;
    window.clearInterval(this.pingConversationIntervalId);
    this.pingConversationIntervalId = window.setInterval(function () {
      that.ping.call(that);
    }, this.POLL_QUEUE_SECONDS * 1000);
  }

  async ping() {
    if (this.conversationId == null) return;

    const response = await this.chat.ping(this.conversationId, this.role);
    const conversation = response.conversation;

    this.guestName = conversation.guestName;
    this.supportName = conversation.supportName;

    for (const message of conversation.messages) {
      if (message.id > this.lastMessageId) {
        this.lastMessageId = message.id;
        let name: string;
        switch (message.role) {
          case this.ROLE_GUEST:
            name = conversation.guestName;
            break;
          case this.ROLE_SUPPORT:
            name = conversation.supportName;
            break;
          case this.ROLE_SYSTEM:
          default:
            name = '';
        }
        this.appendComment(message.role, name, message.text);
      }
    }

    const disconnected =
      conversation.endTime != null ||
      (this.role == this.ROLE_GUEST &&
        response.secondsSinceLastSupportPing >
          this.DISCONNECTED_WAIT_SECONDS) ||
      (this.role == this.ROLE_SUPPORT &&
        response.secondsSinceLastGuestPing > this.DISCONNECTED_WAIT_SECONDS);

    if (disconnected && this.role == this.ROLE_SUPPORT) {
      await this.unjoin();
    } else {
      await this.end();
    }
  }

  appendComment(role: string, name: string, str: string) {
    this.chatWindowDiv.innerHTML =
      this.chatWindowDiv.innerHTML + this.wrapChatComment(role, name, str);
    this.chatWindow.nativeElement.scrollTop =
      this.chatWindow.nativeElement.scrollHeight;
  }

  wrapChatComment(role: string, name: string, str: string) {
    let nameHTML = `<span style="color:var(--colorImgs11); font-size:.75em">(${name} - ${role})</span>`;

    if (name == '') {
      nameHTML = '';
    }

    let css1 = '',
      css2;
    if (name === this.name) {
      css1 = 'align-self: flex-end;';
      css2 = 'background-color: var(--colorBen); color: var(--colorCal);';
    } else {
      css2 = 'background-color: var(--colorCal); color: var(--colorBen);';
    }

    // TODO (jacob): Fix the css bug. renderer or viewencapsulation?
    // the class applied here wasn't being applied properly, so I inlined it
    return `
            <div style="width:40%; ${css1}">
                ${nameHTML}
                <div style="border-radius:1em; ${css2} padding:.5em;margin-bottom:.5em">
                    <span>${str}</span>
                </div>
            </div>`;
  }
}
